import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  UserOutlined,
  ShoppingOutlined,
  EnvironmentOutlined,
  LogoutOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  LockOutlined,
  CreditCardOutlined,
  PhoneOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import MyAccountSkeleton from "../components/MyAccountSkeleton";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";

const Container = styled.div`
  /* min-height: 100vh; */
  background: #fff;
`;

const Wrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #000;
  margin: 0 0 40px 0;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 30px;
  }
`;

// Profile Section
const ProfileSection = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    padding: 20px;
    margin-bottom: 30px;
  }
`;

const ProfileAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: #fff;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
    font-size: 36px;
  }
`;

const ProfileInfo = styled.div`
  flex: 1;

  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #000;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0 0 4px 0;
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 20px;
    }
  }
`;

const LogoutButton = styled.button`
  padding: 12px 24px;
  background: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #000;

  &:hover {
    background: #e0e0e0;
    border-color: #ccc;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

// Account Options Grid
const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 50px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const OptionCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  gap: 16px;
  align-items: flex-start;

  &:hover {
    border-color: #ddd;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 20px;
    gap: 12px;
  }
`;

const OptionIconBox = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: ${(props) => props.bgColor || "#f9f9f9"};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: ${(props) => props.color || "#000"};
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    font-size: 24px;
  }
`;

const OptionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #000;
  margin: 0 0 6px 0;

  @media (max-width: 768px) {
    font-size: 15px;
  }
`;

const OptionDescription = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

const OptionArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  flex-shrink: 0;
`;

// Modal Styles
const Modal = styled.div`
  display: ${(props) => (props.open ? "flex" : "none")};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ModalHeader = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;

  &:hover {
    color: #000;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #000;
  }

  input,
  textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 13px;
    font-family: inherit;
    transition: all 0.3s;

    &:focus {
      outline: none;
      border-color: #000;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;

  button {
    flex: 1;
    padding: 12px 24px;
    border-radius: 6px;
    border: none;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s;
  }
`;

const PrimaryButton = styled.button`
  background: #000;
  color: #fff;

  &:hover {
    background: #333;
  }
`;

const SecondaryButton = styled.button`
  background: #f0f0f0;
  color: #000;
  border: 1px solid #ddd;

  &:hover {
    background: #e0e0e0;
  }
`;

// List Styles
const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ListItem = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const ListItemContent = styled.div`
  flex: 1;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #000;
    margin: 0 0 6px 0;
  }

  p {
    font-size: 13px;
    color: #666;
    margin: 0;
    line-height: 1.5;
  }
`;

const ListItemActions = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  background: none;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 12px;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    border-color: #000;
    background: #f9f9f9;
  }

  &.delete:hover {
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const MyAccount = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const [userData, setUserData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [orders, setOrders] = useState([
    {
      id: "ORD-2025-001234",
      date: "Feb 3, 2025",
      status: "Delivered",
      items: "Nike Air Force 1 (x2), Classic white sneaker (x1)",
      total: "₹29,278",
    },
    {
      id: "ORD-2025-001233",
      date: "Jan 28, 2025",
      status: "In Transit",
      items: "Summer Collection (x3)",
      total: "₹15,450",
    },
  ]);

  const [addresses, setAddresses] = useState([
    {
      id: 1,
      name: "Home",
      street: "123 MG Road",
      city: "Bangalore",
      state: "Karnataka",
      pincode: "560001",
      phone: "9876543210",
    },
    {
      id: 2,
      name: "Office",
      street: "456 Tech Park",
      city: "Bangalore",
      state: "Karnataka",
      pincode: "560029",
      phone: "9876543211",
    },
  ]);

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (!token) {
          navigate("/login");
          return;
        }
        const resp = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/auth/me`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );
        const data = await resp.json();
        if (data.status && data.data && data.data.user) {
          const user = data.data.user;
          setUserData({
            firstName: user.name?.split(" ")?.[0] || "",
            lastName: user.name?.split(" ")?.[1] || "",
            email: user.email || "",
            phone: user.phone || "",
          });
          // Set addresses if available
          if (user.addresses && user.addresses.length > 0) {
            setAddresses(
              user.addresses.map((addr) => ({
                id: addr.id,
                name: addr.name,
                street: addr.address,
                city: addr.city,
                state: addr.state,
                pincode: addr.pincode,
                phone: addr.phone,
              })),
            );
          }
        }
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token, navigate]);

  const [formData, setFormData] = useState({
    firstName: userData.firstName,
    lastName: userData.lastName,
    email: userData.email,
    phone: userData.phone,
  });

  const [addressForm, setAddressForm] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    pincode: "",
    phone: "",
  });

  const accountOptions = [
    {
      id: "orders",
      title: "Your Orders",
      description: "Track, return, or buy things again",
      icon: ShoppingOutlined,
      bgColor: "#fff3e0",
      color: "#f57c00",
      to: "/orders",
    },
    // {
    //   id: "security",
    //   title: "Login & Security",
    //   description: "Edit login, name, and mobile number",
    //   icon: LockOutlined,
    //   bgColor: "#f3e5f5",
    //   color: "#7b1fa2",
    // },
    {
      id: "addresses",
      title: "Your Addresses",
      description: "Edit addresses for orders and gifts",
      icon: EnvironmentOutlined,
      bgColor: "#e0f2f1",
      color: "#00796b",
      to: "/addresses",
    },
    // {
    //   id: "payment",
    //   title: "Payment Options",
    //   description: "Edit or add payment methods",
    //   icon: CreditCardOutlined,
    //   bgColor: "#e3f2fd",
    //   color: "#1565c0",
    // },
    // {
    //   id: "contact",
    //   title: "Contact Us",
    //   description: "Contact our customer service via phone or chat",
    //   icon: PhoneOutlined,
    //   bgColor: "#f1f8e9",
    //   color: "#558b2f",
    // },
    {
      id: "personal",
      title: "Personal Information",
      description: "View and edit your personal details",
      icon: UserOutlined,
      bgColor: "#ede7f6",
      color: "#512da8",
      to: "/personal-info",
    },
  ];

  const handleOpenModal = (modalId) => {
    setActiveModal(modalId);
    setEditingIndex(null);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setEditingIndex(null);
    setAddressForm({
      name: "",
      street: "",
      city: "",
      state: "",
      pincode: "",
      phone: "",
    });
  };

  const handleAddAddress = () => {
    if (addressForm.name && addressForm.street && addressForm.city) {
      if (editingIndex !== null) {
        const updatedAddresses = [...addresses];
        updatedAddresses[editingIndex] = {
          ...addresses[editingIndex],
          ...addressForm,
        };
        setAddresses(updatedAddresses);
      } else {
        setAddresses([...addresses, { id: Date.now(), ...addressForm }]);
      }
      handleCloseModal();
    }
  };

  const handleDeleteAddress = (id) => {
    setAddresses(addresses.filter((addr) => addr.id !== id));
  };

  const handleEditAddress = (index) => {
    setEditingIndex(index);
    setAddressForm(addresses[index]);
    setActiveModal("addresses");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressInputChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <>
        {/* <Navbar /> */}
        <MyAccountSkeleton />
        <Footer />
      </>
    );
  }

  return (
    <Container>
      {/* <Navbar /> */}

      <Wrapper>
        <PageTitle>Your Account</PageTitle>

        {/* Profile Section */}
        <ProfileSection>
          <ProfileAvatar>
            {userData.firstName.charAt(0)}
            {userData.lastName.charAt(0)}
          </ProfileAvatar>
          <ProfileInfo>
            <h2>
              {userData.firstName} {userData.lastName}
            </h2>
            <p>{userData.email}</p>
            <p>{userData.phone}</p>
          </ProfileInfo>
          <LogoutButton
            onClick={() => {
              dispatch({ type: "logout" });
            }}
          >
            <LogoutOutlined />
            Logout
          </LogoutButton>
        </ProfileSection>

        {/* Account Options Grid */}
        <OptionsGrid>
          {accountOptions.map((option) => {
            const IconComponent = option.icon;
            return (
              <OptionCard key={option.id} onClick={() => navigate(option.to)}>
                <OptionIconBox bgColor={option.bgColor} color={option.color}>
                  <IconComponent />
                </OptionIconBox>
                <OptionContent>
                  <OptionTitle>{option.title}</OptionTitle>
                  <OptionDescription>{option.description}</OptionDescription>
                </OptionContent>
                <OptionArrow>
                  <ArrowRightOutlined />
                </OptionArrow>
              </OptionCard>
            );
          })}
        </OptionsGrid>
      </Wrapper>

      {/* Login & Security Modal */}
      <Modal open={activeModal === "security"}>
        <ModalContent>
          <ModalHeader>
            <span>Login & Security</span>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>
          <FormGroup>
            <label>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <label>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              defaultValue="password123"
            />
          </FormGroup>
          <ButtonGroup>
            <PrimaryButton>Update</PrimaryButton>
            <SecondaryButton onClick={handleCloseModal}>Cancel</SecondaryButton>
          </ButtonGroup>
        </ModalContent>
      </Modal>

      {/* Your Addresses Modal */}
      <Modal open={activeModal === "addresses"}>
        <ModalContent>
          <ModalHeader>
            <span>
              {editingIndex !== null ? "Edit Address" : "Add Address"}
            </span>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>
          <FormGroup>
            <label>Address Name (Home, Office, etc.)</label>
            <input
              type="text"
              name="name"
              value={addressForm.name}
              onChange={handleAddressInputChange}
              placeholder="e.g., Home, Office"
            />
          </FormGroup>
          <FormGroup>
            <label>Street Address</label>
            <input
              type="text"
              name="street"
              value={addressForm.street}
              onChange={handleAddressInputChange}
              placeholder="123 Main Street"
            />
          </FormGroup>
          <FormGroup>
            <label>City</label>
            <input
              type="text"
              name="city"
              value={addressForm.city}
              onChange={handleAddressInputChange}
              placeholder="Bangalore"
            />
          </FormGroup>
          <FormGroup>
            <label>State</label>
            <input
              type="text"
              name="state"
              value={addressForm.state}
              onChange={handleAddressInputChange}
              placeholder="Karnataka"
            />
          </FormGroup>
          <FormGroup>
            <label>Pincode</label>
            <input
              type="text"
              name="pincode"
              value={addressForm.pincode}
              onChange={handleAddressInputChange}
              placeholder="560001"
            />
          </FormGroup>
          <FormGroup>
            <label>Phone Number</label>
            <input
              type="tel"
              name="phone"
              value={addressForm.phone}
              onChange={handleAddressInputChange}
              placeholder="9876543210"
            />
          </FormGroup>
          <ButtonGroup>
            <PrimaryButton onClick={handleAddAddress}>
              {editingIndex !== null ? "Update Address" : "Add Address"}
            </PrimaryButton>
            <SecondaryButton onClick={handleCloseModal}>Cancel</SecondaryButton>
          </ButtonGroup>

          {activeModal === "addresses" && (
            <>
              <div
                style={{
                  marginTop: "32px",
                  paddingTop: "24px",
                  borderTop: "1px solid #eee",
                }}
              >
                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: 700,
                    margin: "0 0 16px 0",
                  }}
                >
                  Saved Addresses
                </h3>
                <ItemsList>
                  {addresses.map((address, index) => (
                    <ListItem key={address.id}>
                      <ListItemContent>
                        <h4>{address.name}</h4>
                        <p>{address.street}</p>
                        <p>
                          {address.city}, {address.state} {address.pincode}
                        </p>
                        <p>{address.phone}</p>
                      </ListItemContent>
                      <ListItemActions>
                        <ActionButton onClick={() => handleEditAddress(index)}>
                          <EditOutlined />
                          Edit
                        </ActionButton>
                        <ActionButton
                          className="delete"
                          onClick={() => handleDeleteAddress(address.id)}
                        >
                          <DeleteOutlined />
                          Delete
                        </ActionButton>
                      </ListItemActions>
                    </ListItem>
                  ))}
                </ItemsList>
              </div>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Payment Options Modal */}
      <Modal open={activeModal === "payment"}>
        <ModalContent>
          <ModalHeader>
            <span>Payment Options</span>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>
          <ItemsList>
            <ListItem>
              <ListItemContent>
                <h4>Credit/Debit Card</h4>
                <p>Visa ending in 4242</p>
              </ListItemContent>
              <ListItemActions>
                <ActionButton>Edit</ActionButton>
                <ActionButton className="delete">Delete</ActionButton>
              </ListItemActions>
            </ListItem>
            <ListItem>
              <ListItemContent>
                <h4>UPI</h4>
                <p>john.doe@upi</p>
              </ListItemContent>
              <ListItemActions>
                <ActionButton>Edit</ActionButton>
                <ActionButton className="delete">Delete</ActionButton>
              </ListItemActions>
            </ListItem>
          </ItemsList>
          <PrimaryButton style={{ width: "100%", marginTop: "20px" }}>
            + Add New Payment Method
          </PrimaryButton>
        </ModalContent>
      </Modal>

      {/* Contact Us Modal */}
      <Modal open={activeModal === "contact"}>
        <ModalContent>
          <ModalHeader>
            <span>Contact Us</span>
            <CloseButton onClick={handleCloseModal}>×</CloseButton>
          </ModalHeader>
          <ItemsList>
            <ListItem>
              <ListItemContent>
                <h4>Customer Service Phone</h4>
                <p>+91-1234-567-890</p>
                <p style={{ marginTop: "8px", fontSize: "12px" }}>
                  Available 24/7
                </p>
              </ListItemContent>
            </ListItem>
            <ListItem>
              <ListItemContent>
                <h4>Email Support</h4>
                <p>support@ecommerce.com</p>
                <p style={{ marginTop: "8px", fontSize: "12px" }}>
                  Response within 24 hours
                </p>
              </ListItemContent>
            </ListItem>
            <ListItem>
              <ListItemContent>
                <h4>Live Chat</h4>
                <p>Available on website</p>
                <p style={{ marginTop: "8px", fontSize: "12px" }}>
                  Monday-Friday, 9am-6pm
                </p>
              </ListItemContent>
            </ListItem>
          </ItemsList>
          <PrimaryButton
            onClick={handleCloseModal}
            style={{ width: "100%", marginTop: "20px" }}
          >
            Open Chat
          </PrimaryButton>
        </ModalContent>
      </Modal>

      <Footer />
    </Container>
  );
};

export default MyAccount;
